import { Label } from "layout/typography/Label";
import { InputSwitch } from "primereact/inputswitch";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { IReceiptContent } from "types/Customize";
import { OrderType } from "types/Order/OrderType";

import { StyledCard } from "../styled";
import {
  StyledSettingRow,
  StyledSettingsCard,
  StyledSettingsColumn,
  StyledSettingsDescription,
  StyledSettingsHeader,
  StyledSettingSubtitle,
  StyledSettingTitle,
} from "./preview";

interface IReceiptContentFields {
  "GFE-version2Receipt"?: boolean;
  "Bundled-version2Receipt"?: boolean;
  "Bariatrics-version2Receipt"?: boolean;
  "ED-version2Receipt"?: boolean;
  "PatientResponsibility-version2Receipt"?: boolean;
}

const receiptSettings = [
  {
    field: "version2Receipt",
    title: "Version 2 Receipt",
    subtitle: "Add the charge breakdown section to the receipt.",
  },
];

export function ReceiptForm({
  customFields,
  orderType,
}: {
  customFields?: IReceiptContent;
  orderType: OrderType;
}) {
  const { control, setValue } = useFormContext<IReceiptContentFields>();

  const savedVersion2Receipt = customFields?.version2Receipt;

  // Depends on the saved value, not on the customFields object: an unstable
  // prop would otherwise re-run setValue on every render and loop.
  useEffect(() => {
    setValue(`${orderType}-version2Receipt`, savedVersion2Receipt || false);
  }, [orderType, savedVersion2Receipt, setValue]);

  return (
    <StyledCard
      data-test-id="customize-receipt-content"
      title="Order Receipt Version"
    >
      <StyledSettingsColumn>
        <StyledSettingsCard>
          <StyledSettingsHeader>
            <i className="pi pi-sliders-h" />
            RECEIPT SETTINGS
          </StyledSettingsHeader>

          <StyledSettingsDescription>
            Choose what the receipt patients get after paying. Applies to Clear
            Estimate Pro orders only.
          </StyledSettingsDescription>

          {receiptSettings.map((setting) => (
            <StyledSettingRow key={setting.field}>
              <div>
                <Label
                  htmlFor={`${orderType}-${setting.field}`}
                  data-test-id={`${orderType}-${setting.field}`}
                >
                  <StyledSettingTitle>{setting.title}</StyledSettingTitle>
                </Label>
                <StyledSettingSubtitle>
                  {setting.subtitle}
                </StyledSettingSubtitle>
              </div>

              <Controller
                name={
                  `${orderType}-${setting.field}` as keyof IReceiptContentFields
                }
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSwitch
                    inputId={`${orderType}-${setting.field}`}
                    data-test-id={`${orderType}-${setting.field}-switch`}
                    checked={!!value}
                    onChange={(event) => onChange(event.value)}
                  />
                )}
              />
            </StyledSettingRow>
          ))}
        </StyledSettingsCard>
      </StyledSettingsColumn>
    </StyledCard>
  );
}
